import {
  Box,
  Button,
  Columns,
  Container,
  Heading,
  Stack,
  Text,
  Visible,
} from '@spaceship-fspl/components';
import { ExternalRoutes } from '@spaceship-fspl/helpers';
import { SuperProductIcon } from '@spaceship-fspl/icons-web';
import { linearGradient, width } from '@spaceship-fspl/styles';
import { TrackingCategory } from '@spaceship-fspl/tracking';
import { PanelSection } from 'components';
import signupDesktopImage from 'images/first-home_signup-desktop.svg';
import stateSignupDesktopImage from 'images/first-home-state_signup-desktop.png';
import stateSignupMobileImage from 'images/first-home-state_signup-mobile.png';
import aaaRatingBadgeImage from 'images/spaceship-super_aaa-rating-badge.svg';
import moneyMagBadgeImage from 'images/spaceship-super_money-mag-badge.svg';
import React, { HTMLAttributeAnchorTarget } from 'react';
import styled from 'styled-components';

export interface SignUpPanelProps {
  variant: 'homepage' | 'state';
  title?: string | null;
  tagline?: React.ReactNode;
  disclaimer?: React.ReactNode;
  signupText?: string | null;
  signupUrl?: string | null;
  signupUrlTarget?: HTMLAttributeAnchorTarget;
}

export const SignUpPanel: React.FC<SignUpPanelProps> = ({
  variant,
  title,
  tagline,
  disclaimer,
  signupText,
  signupUrl,
  signupUrlTarget,
}) => {
  if (!signupText) {
    return null;
  }

  return (
    <TrackingCategory category="Signup Panel">
      <StyledPanelSection>
        <Container>
          {variant === 'homepage' && (
            <Box position="relative">
              <Box position="absolute" right={0} top="-16px">
                <Box position="absolute" top={0} right={{ xs: 0, lg: '100px' }}>
                  <StyledBadgeImg src={moneyMagBadgeImage} />
                </Box>

                <Box position="absolute" top={{ xs: '58px', lg: 0 }} right={0}>
                  <StyledBadgeImg src={aaaRatingBadgeImage} />
                </Box>
              </Box>
            </Box>
          )}

          <Columns alignX="center">
            <Columns.Column width={{ xs: 1, md: 6 / 8, lg: 10 / 12 }}>
              <Stack
                spaceY={
                  variant === 'homepage' ? { xs: 'lg', lg: 'sm' } : 'none'
                }
                alignX="center"
              >
                {variant === 'homepage' && (
                  <>
                    <Visible isHidden={{ lg: true }} displayValue="block">
                      <SuperProductIcon color="neutral.000" size="xxxl" />
                    </Visible>

                    <Visible
                      isHidden={{ xs: true, lg: false }}
                      displayValue="block"
                    >
                      <img src={signupDesktopImage} alt={title ?? ''} />
                    </Visible>
                  </>
                )}

                {variant === 'state' && (
                  <>
                    <Visible isHidden={{ lg: true }} displayValue="block">
                      <StyledStateImg
                        src={stateSignupMobileImage}
                        alt={title ?? ''}
                      />
                    </Visible>

                    <Visible
                      isHidden={{ xs: true, lg: false }}
                      displayValue="block"
                    >
                      <StyledStateImg
                        src={stateSignupDesktopImage}
                        alt={title ?? ''}
                      />
                    </Visible>
                  </>
                )}

                <Stack spaceY="lg" alignX="center">
                  <Stack spaceY="md" alignX="center">
                    <Heading
                      component="h2"
                      variant={{ xs: 4, lg: 2 }}
                      color="neutral.000"
                      align="center"
                    >
                      {title}
                    </Heading>

                    {tagline && (
                      <Text
                        variant={{ xs: 2, lg: 1 }}
                        color="neutral.000"
                        align="center"
                        component="div"
                      >
                        {tagline}
                      </Text>
                    )}
                  </Stack>

                  <Button
                    href={signupUrl ?? ExternalRoutes.SUPER_SIGNUP}
                    target={
                      signupUrl && signupUrlTarget ? signupUrlTarget : undefined
                    }
                    size="lg"
                    variant="tertiary"
                  >
                    {signupText}
                  </Button>

                  {disclaimer && (
                    <Text
                      variant={3}
                      color="neutral.000"
                      align="center"
                      component="div"
                    >
                      {disclaimer}
                    </Text>
                  )}
                </Stack>
              </Stack>
            </Columns.Column>
          </Columns>
        </Container>
      </StyledPanelSection>
    </TrackingCategory>
  );
};

const StyledPanelSection = styled(PanelSection)`
  background: ${linearGradient({
    angle: '31deg',
    colorStops: [
      ['indigo.100', '0%'],
      ['indigo.070', '113.19%'],
    ],
  })};
`;

const StyledBadgeImg = styled.img`
  ${width({ xs: 64, lg: 115 })}
`;

const StyledStateImg = styled.img`
  ${width({ xs: 300, lg: 477 })}
`;
